import axios from "axios";
import React, { useState, useEffect } from "react";
import { BASE_URL } from "../../../env";
import Sidebar from "../../../components/layout/Sidebar";
import { Form, Row, Col } from "react-bootstrap";
import {
  getFirstAndLastEmiDates,
  formatAmount,
  isLoanCovered,
  resolveEffectiveStartDate,
} from "../../../utils/utils";
import CustomPagination from "../../../components/ui/CustomPagination";
import DataTable from "../../../components/ui/DataTable";
import { useAdminAuth } from "../../auth/AdminAuthProvider";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import { MdExpandMore } from "react-icons/md";
import StandardizedDateInput from "../../../components/ui/StandardizedDateInput";

function GplInsuranceReport() {
  const { adminUser } = useAdminAuth();

  const [reportData, setReportData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);

  const itemsPerPage = 20;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginationData = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const OpenSidebar = () => {
    setOpenSidebarToggle(!openSidebarToggle);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/getgplinsurencereport`,
          { params: { id: adminUser.user.id } }
        );
        if (response.data) {
          setReportData(response.data);
        }
      } catch (error) {
        console.error("Error fetching insurance report data:", error);
      }
    };
    fetchData();
  }, [adminUser.user.id]);

  useEffect(() => {
    let filtered = reportData;

    if (fromDate || toDate) {
      const start = new Date(fromDate || 0);
      start.setHours(0, 0, 0, 0);
      const end = fromDate && toDate ? new Date(toDate) : new Date();
      end.setHours(23, 59, 59, 999);

      filtered = filtered.filter((item) => {
        if (!item.branchManagerStatusUpdatedAt) return false;
        const updatedAt = new Date(item.branchManagerStatusUpdatedAt);
        return updatedAt >= start && updatedAt <= end;
      });
    }

    if (searchTerm && searchTerm.trim() !== "") {
      const term = searchTerm.trim().toLowerCase();
      filtered = filtered.filter((item) => {
        const memberName = String(item?.memberName || "").toLowerCase();
        const customerId = String(item?.customerId || "").toLowerCase();
        const loanId = String(item?.loanId || "").toLowerCase();
        const phoneNumber = String(item?.phoneNumber || "").toLowerCase();
        const aadharNo = String(item?.aadharNo || "").toLowerCase();
        const applicationId = String(item?.ApplicationId || "").toLowerCase();

        return (
          memberName.includes(term) ||
          customerId.includes(term) ||
          loanId.includes(term) ||
          phoneNumber.includes(term) ||
          aadharNo.includes(term) ||
          applicationId.includes(term)
        );
      });
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [fromDate, toDate, reportData, searchTerm]);

  const ageCalculator = (dobString) => {
    if (!dobString) return "";
    const dob = new Date(dobString);
    if (isNaN(dob.getTime())) return "";
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    if (
      today.getMonth() < dob.getMonth() ||
      (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())
    ) {
      age--;
    }
    return age;
  };

  const columns = React.useMemo(() => [
    { label: "S No", render: (data, index, isExcel) => isExcel ? index + 1 : indexOfFirstItem + index + 1 },
    { label: "Branch Code", render: (data) => data.branchCode || "" },
    { label: "Sub Category", render: (data) => data.businessDetails?.natureOfBusiness || "" },
    { label: "Loan Account No", render: (data) => data.loanId || "" },
    { label: "Serial Number", render: (data) => data.loanCycle || 1 },
    { label: "Member Name", render: (data) => data.memberName || "" },
    { label: "Member Guardian Name", render: (data) => data.familyMember?.coApplicantName || "" },
    { label: "Member DOB", render: (data) => data.dateOfBirth || "" },
    { label: "Gender", render: (data) => data.gender || "" },
    { label: "Document Type", render: () => "Aadhar" },
    { label: "Document Number", render: (data) => data.aadharNo || "" },
    { label: "Relationship from Primary", render: () => "Self" },
    { label: "Occupation", render: (data) => data.businessDetails?.natureOfBusiness || "Business" },
    { label: "Mobile No", render: (data) => data.phoneNumber || "" },
    { label: "Nominee Name", render: (data) => data.familyMember?.coApplicantName || "" },
    { label: "Nominee DOB", render: (data) => data.familyMember?.dateOfBirth || "" },
    {
      label: "Nominee Age",
      render: (data) => ageCalculator(data.familyMember?.dateOfBirth),
    },
    { label: "Nominee Gender", render: (data) => data.familyMember?.gender || "" },
    { label: "Nominee Relation", render: (data) => data.familyMember?.relation || "" },
    {
      label: "Nominee Minor",
      render: (data) => {
        const age = ageCalculator(data.familyMember?.dateOfBirth);
        return age === "" ? "" : age > 18 ? "NO" : "YES";
      },
    },
    { label: "Nominee Guardian Name", render: (data) => data.memberName || "" },
    {
      label: "Nominee Guardian Age",
      render: (data) => ageCalculator(data.dateOfBirth),
    },
    {
      label: "Relation with Nominee",
      render: (data) => {
        const { relation, gender } = data.familyMember ?? {};
        if (!relation || !gender) return "Self/Relative";
        const map = {
          Son: gender === "male" ? "Father" : "Mother",
          Daughter: gender === "male" ? "Father" : "Mother",
          Spouse: "Spouse",
          Father: gender === "male" ? "Son" : "Daughter",
          Mother: gender === "male" ? "Son" : "Daughter",
          Brother: gender === "male" ? "Brother" : "Sister",
          "Father-in-Law": gender === "male" ? "Son-in-Law" : "Daughter-in-Law",
          "Mother-in-Law": gender === "male" ? "Son-in-Law" : "Daughter-in-Law",
        };
        return map[relation] ?? "Guardian";
      },
    },
    { label: "Loan Amount", render: (data) => formatAmount(data.eligibleLoanAmountByBm) },
    {
      label: "Start Date",
      render: (data) => {
        const { firstEmiDate } = getFirstAndLastEmiDates(
          data.branchManagerStatusUpdatedAt,
          data.proposedLoanDetails?.tenureInMonths,
          data.emiDateByBranchManager,
          resolveEffectiveStartDate(data)
        );
        return firstEmiDate ? firstEmiDate.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-";
      },
    },
    {
      label: "Maturity Date",
      render: (data) => {
        const { lastEmiDate } = getFirstAndLastEmiDates(
          data.branchManagerStatusUpdatedAt,
          data.proposedLoanDetails?.tenureInMonths,
          data.emiDateByBranchManager,
          resolveEffectiveStartDate(data)
        );
        return lastEmiDate ? lastEmiDate.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-";
      },
    },
    { label: "Premium", render: (data) => formatAmount(isLoanCovered(data) ? data.insuranceAmount : 0) },
    { label: "Tenure", render: (data) => `${data.proposedLoanDetails?.tenureInMonths || 0} Months` },
    { label: "Loan Type", render: (data) => data.loanType || "" },
    {
      label: "Address",
      render: (data) => {
        const parts = [
          data.permanentDoorNo,
          data.permanentStreetNo,
          data.permanentPanchayat,
          data.permanentTaluk,
          data.permanentDistrict,
          data.permanentPincode
        ].filter(part => part && String(part).trim() !== "");
        return parts.length > 0 ? parts.join(", ") : "N/A";
      },
    },
    { label: "Pincode", render: (data) => data.permanentPincode || "" },
  ], [indexOfFirstItem]);

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar
          openSidebarToggle={openSidebarToggle}
          OpenSidebar={OpenSidebar}
        />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={OpenSidebar} />
        </div>

        <div className="master-card-container">
          <div className="master-search-section">
            <h1 className="TableHeaderText w-100 text-start">Insurance Report</h1>
            <button
              className={`filter-toggle-btn ${showMobileFilters ? "active" : ""}`}
              onClick={() => setShowMobileFilters(!showMobileFilters)}
            >
              <span style={{ fontWeight: 600, fontSize: "14px" }}>
                {showMobileFilters ? "Hide Filters" : "Show Filters"}
              </span>
              <MdExpandMore size={22} />
            </button>

            <div className={`w-100 ${!showMobileFilters ? "mobile-hidden" : "mobile-visible"}`}>
              <Row className="w-100" style={{ fontWeight: "500" }}>
                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>Search Customer</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search Name, Customer ID, Loan Acc No, Phone..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={2} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>From Date</Form.Label>
                    <StandardizedDateInput
                      value={fromDate}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFromDate(val);
                        if (val && toDate) {
                          setTimeout(() => setShowMobileFilters(false), 300);
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={2} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>To Date</Form.Label>
                    <StandardizedDateInput
                      value={toDate}
                      onChange={(e) => {
                        const val = e.target.value;
                        setToDate(val);
                        if (fromDate && val) {
                          setTimeout(() => setShowMobileFilters(false), 300);
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </div>

          <div className="master-list-section">
            <div className="master-table-wrapper" style={{ padding: "0 15px" }}>
              <DataTable
                columns={columns}
                data={paginationData}
                reportName="GplInsuranceReport"
                excelData={filteredData}
              />
            </div>

            <div className="master-pagination-wrapper">
              <CustomPagination
                totalPages={totalPages}
                currentPage={currentPage}
                paginate={paginate}
                totalItems={filteredData.length}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default GplInsuranceReport;
